import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import css from "./finishedScreen.module.css";
import { useTranslation } from "react-i18next";

const ArrowSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
);

const CompletionScreen = ({ lesson, onRepeat }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const [fb, setFb] = useState(null);

  const score = lesson?.score || 86;
  const passThreshold = 70;
  const lessonName = lesson?.video?.name || 'Lesson';
  const nextLessonName = lesson?.nextLesson?.name || 'Next Lesson';
  const nextLessonDesc = lesson?.nextLesson?.description || '';
  const wordsLearned = lesson?.wordsLearned || 12;
  const wordsSaved = lesson?.wordsSaved || 5;
  const quizScore = lesson?.quizScore || '90%';
  const speakingScore = lesson?.speakingScore || '82%';

  // Ring calculations: circumference = 2πr = 2 * π * 47 ≈ 295
  const circumference = 295;
  const ringTarget = circumference - (circumference * (score / 100));

  const handleNextLesson = () => {
    const currentId = parseInt(id);
    const nextId = currentId + 1;
    navigate(`/lesson/${nextId}`);
    window.location.reload();
  };

  const handleRepeatLesson = () => {
    if (onRepeat) {
      onRepeat();
    }
  };

  return (
    <div className={css.wrap}>
      {/* HERO */}
      <div className={css.hero}>
        <div className={css.sparkles}>
          <div className={css.sp}></div><div className={css.sp}></div>
          <div className={css.sp}></div><div className={css.sp}></div>
        </div>
        <div className={css.sucIcon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
        </div>
        <h1>{t('lessonComplete.title', 'Блискуче! Урок зараховано')}</h1>
        <p>{t('lessonComplete.subtitle', 'Ви чудово впоралися — наступний урок уже відкрито.')}</p>
        <p className={css.praise}>{t('lessonComplete.praise', 'Ще один крок до впевненої англійської.')}</p>
      </div>

      {/* RESULT */}
      <div className={css.result}>
        <div className={css.scoreRing}>
          <svg viewBox="0 0 110 110">
            <defs><linearGradient id="scoreGradGreen" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#34D399"/><stop offset="100%" stopColor="#10B981"/></linearGradient></defs>
            <circle className={css.ringBg} cx="55" cy="55" r="47"/>
            <circle className={css.ringPass} cx="55" cy="55" r="47" strokeDasharray="4 4" strokeDashoffset="88.5" fill="none"/>
            <circle className={css.ringFill} cx="55" cy="55" r="47" stroke="url(#scoreGradGreen)" fill="none" style={{'--ring-target': ringTarget}}/>
          </svg>
          <div className={css.scoreNum}>
            <span className={`${css.scoreVal} ${css.scoreValGreen}`}>{score}%</span>
            <span className={css.scoreLbl}>{t('lessonComplete.result', 'Результат')}</span>
          </div>
        </div>
        <div className={css.resultInfo}>
          <div className={css.resultLesson}>{lessonName}</div>
          <div className={css.resultMeta}>
            <div className={`${css.rm} ${css.rmPass}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>
              {t('lessonComplete.passReq', 'Потрібно для зарахування:')} {passThreshold}%
            </div>
            <div className={css.rmStatusGreen}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
              {t('lessonComplete.passed', 'Зараховано')}
            </div>
          </div>
        </div>
      </div>

      {/* NEXT LESSON */}
      <div className={css.nextCard} onClick={handleNextLesson}>
        <div className={css.nextIcon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </div>
        <div className={css.nextBody}>
          <div className={css.nextLabel}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            {t('lessonComplete.nextUnlocked', 'Наступний урок відкрито')}
          </div>
          <div className={css.nextTitle}>{nextLessonName}</div>
          <div className={css.nextDesc}>{nextLessonDesc}</div>
        </div>
        <button className={css.nextCta} onClick={(e) => { e.stopPropagation(); handleNextLesson(); }}>
          {t('lessonComplete.go', 'Перейти')}<ArrowSvg />
        </button>
      </div>

      {/* BREAKDOWN */}
      <div className={css.breakdown}>
        <div className={css.bdCard}>
          <div className={`${css.bdIcon} ${css.bdiV}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg></div>
          <div className={css.bdLabel}>Video</div>
          <div className={css.bdValue}>{t('lessonComplete.watched', 'Переглянуто')}</div>
        </div>
        <div className={css.bdCard}>
          <div className={`${css.bdIcon} ${css.bdiW}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg></div>
          <div className={css.bdLabel}>Words</div>
          <div className={css.bdValue}>{wordsLearned} {t('lessonComplete.words', 'слів')}</div>
        </div>
        <div className={css.bdCard}>
          <div className={`${css.bdIcon} ${css.bdiQ}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg></div>
          <div className={css.bdLabel}>{t("Quiz")}</div>
          <div className={css.bdValue}>{quizScore}</div>
        </div>
        <div className={css.bdCard}>
          <div className={`${css.bdIcon} ${css.bdiS}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg></div>
          <div className={css.bdLabel}>Speaking</div>
          <div className={css.bdValue}>{speakingScore}</div>
        </div>
      </div>

      {/* WORDS */}
      <div className={css.wordsRow}>
        <div className={css.wordCard}>
          <div className={`${css.wcIcon} ${css.wci1}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg></div>
          <div className={css.wcBody}>
            <div className={css.wcTitle}>{t('lessonComplete.wordsLearned', 'Вивчені слова')}</div>
            <div className={css.wcVal}>{wordsLearned}</div>
            <div className={css.wcDesc}>{t('lessonComplete.wordsLearnedDesc', 'Усі слова з діалогу цього уроку.')}</div>
          </div>
        </div>
        <div className={css.wordCard}>
          <div className={`${css.wcIcon} ${css.wci2}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg></div>
          <div className={css.wcBody}>
            <div className={css.wcTitle}>{t('lessonComplete.wordsSaved', 'Слова у словнику')}</div>
            <div className={css.wcVal}>{wordsSaved}</div>
            <div className={css.wcDesc}>{t('lessonComplete.wordsSavedDesc', 'Слова, додані у власний словник.')}</div>
          </div>
        </div>
      </div>

      {/* BACK + FEEDBACK */}
      <div className={css.bottomRow}>
        <button className={css.backLink} onClick={() => navigate('/roadMap')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
          {t('lessonComplete.backToPlan', 'Повернутися до плану')}
        </button>
        <div className={css.feedback}>
          {t('lessonComplete.useful', 'Чи був цей урок корисним?')}
          <button className={`${css.fbBtn} ${fb === 'up' ? css.fbBtnActive : ''}`} onClick={() => setFb('up')}>👍</button>
          <button className={`${css.fbBtn} ${fb === 'down' ? css.fbBtnActive : ''}`} onClick={() => setFb('down')}>👎</button>
        </div>
      </div>
    </div>
  );
};

export default CompletionScreen;
